import { api } from './api';
import { ApiResponse } from '../types';

export interface EmailData {
  to: string;
  subject: string;
  html: string;
  from?: string;
  attachments?: EmailAttachment[];
}

export interface EmailAttachment {
  filename: string;
  content: string;
  contentType: string;
}

export interface EmailResponse {
  id: string;
  messageId: string;
  status: 'sent' | 'failed' | 'pending';
  sentAt?: string;
  error?: string;
}

export interface EmailHistory {
  id: number;
  to: string;
  from: string;
  subject: string;
  html: string;
  status: 'sent' | 'failed' | 'pending';
  messageId?: string;
  error?: string;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
}

class EmailService {
  /**
   * Enviar email
   */
  async sendEmail(emailData: EmailData): Promise<ApiResponse<EmailResponse>> {
    try {
      const response = await api.post('/emails/send', emailData);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao enviar email:', error);
      throw new Error(error.response?.data?.message || 'Erro ao enviar email');
    }
  }

  /**
   * Obter histórico de emails
   */
  async getEmailHistory(params?: {
    page?: number;
    limit?: number;
    to?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<EmailHistory[]>> {
    try {
      const response = await api.get('/emails/history', { params });
      return response.data;
    } catch (error: any) {
      console.error('Erro ao buscar histórico de emails:', error);
      throw new Error(error.response?.data?.message || 'Erro ao buscar histórico de emails');
    }
  }

  /**
   * Obter detalhes de um email específico
   */
  async getEmailById(id: number): Promise<ApiResponse<EmailHistory>> {
    try {
      const response = await api.get(`/emails/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao buscar email:', error);
      throw new Error(error.response?.data?.message || 'Erro ao buscar email');
    }
  }

  /**
   * Reenviar email
   */
  async resendEmail(id: number): Promise<ApiResponse<EmailResponse>> {
    try {
      const response = await api.post(`/emails/${id}/resend`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao reenviar email:', error);
      throw new Error(error.response?.data?.message || 'Erro ao reenviar email');
    }
  }

  /**
   * Testar configuração de email
   */
  async testEmailConfig(): Promise<ApiResponse<{ status: string; message: string }>> {
    try {
      const response = await api.post('/emails/test');
      return response.data;
    } catch (error: any) {
      console.error('Erro ao testar configuração de email:', error);
      throw new Error(error.response?.data?.message || 'Erro ao testar configuração de email');
    }
  }

  /**
   * Obter templates de email disponíveis
   */
  async getEmailTemplates(): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get('/emails/templates');
      return response.data;
    } catch (error: any) {
      console.error('Erro ao buscar templates de email:', error);
      throw new Error(error.response?.data?.message || 'Erro ao buscar templates de email');
    }
  }

  /**
   * Validar endereço de email
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Formatar endereço de email para exibição
   */
  formatEmailAddress(email: string, name?: string): string {
    if (name) {
      return `${name} <${email}>`;
    }
    return email;
  }
}

export const emailService = new EmailService();
export default emailService; 